import { bareDomain } from '../../Sales/shared';
import type { CsmReconciliationResponse } from '../../../api/dashboard';

export type UnlinkedCompany = CsmReconciliationResponse['prodCompaniesNotOnList'][number];

/** One row of the "not on the Attio list" table: one or more platform entities that share a name. */
export interface UnlinkedGroup {
  key: string;
  name: string;
  domain: string | null;
  prodCompanyIds: string[];
  totalMembers: number;
  activeMembers30d: number;
  /** Classic + aOS. */
  tokens30d: number;
  /** Undefined when the API predates the aOS field: the figure is then Classic only. */
  aosTokens30d: number | undefined;
  lastActiveAt: string | null;
  /** Set when the Attio list already carries this domain: the workspace is a second one of a listed company. */
  listedAs?: string;
}

const NAME_KEY = (name: string) => name.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '');

/** The same company twice on the platform (two "iBABS" entities) becomes one row, figures summed. */
export function groupUnlinked(rows: readonly UnlinkedCompany[]): UnlinkedGroup[] {
  const byKey = new Map<string, UnlinkedGroup>();
  for (const r of rows) {
    const key = NAME_KEY(r.name) || r.prodCompanyId;
    const aos = r.aosTokens30d;
    const g = byKey.get(key);
    if (!g) {
      byKey.set(key, {
        key,
        name: r.name,
        domain: r.domain,
        prodCompanyIds: [r.prodCompanyId],
        totalMembers: r.totalMembers,
        activeMembers30d: r.activeMembers30d,
        tokens30d: r.tokens30d + (aos ?? 0),
        aosTokens30d: aos,
        lastActiveAt: r.lastActiveAt,
      });
      continue;
    }
    g.prodCompanyIds.push(r.prodCompanyId);
    g.totalMembers += r.totalMembers;
    g.activeMembers30d += r.activeMembers30d;
    g.tokens30d += r.tokens30d + (aos ?? 0);
    g.aosTokens30d = g.aosTokens30d === undefined && aos === undefined ? undefined : (g.aosTokens30d ?? 0) + (aos ?? 0);
    g.domain = g.domain ?? r.domain;
    if (r.lastActiveAt && (!g.lastActiveAt || r.lastActiveAt > g.lastActiveAt)) g.lastActiveAt = r.lastActiveAt;
  }
  return [...byKey.values()];
}

/** A company on the Attio list, as far as the domain match needs it. */
export interface ListedCompany {
  name: string | null;
  domain: string | null;
}

/**
 * Companies first, personal workspaces apart (the API's `personal` flag, the same rule as the aOS and Classic
 * bridges; an API without it sends none), and workspaces whose domain a listed company already carries (Arendt,
 * arendt.com) pulled out as second workspaces instead of "not on the list".
 */
export function splitUnlinked(
  rows: readonly UnlinkedCompany[],
  listed: readonly ListedCompany[] = [],
): { companies: UnlinkedGroup[]; personal: UnlinkedGroup[]; secondWorkspaces: UnlinkedGroup[] } {
  const listedByDomain = new Map<string, string>();
  for (const l of listed) {
    const d = bareDomain(l.domain);
    if (d && l.name && !listedByDomain.has(d)) listedByDomain.set(d, l.name);
  }
  const second: UnlinkedGroup[] = [];
  const rest: UnlinkedCompany[] = [];
  for (const r of rows) {
    const d = bareDomain(r.domain);
    const listedName = d ? listedByDomain.get(d) : undefined;
    if (listedName && !r.personal) second.push({ ...groupUnlinked([r])[0]!, listedAs: listedName });
    else rest.push(r);
  }
  return {
    companies: groupUnlinked(rest.filter((r) => !r.personal)),
    personal: groupUnlinked(rest.filter((r) => r.personal)),
    secondWorkspaces: second,
  };
}
